import React, { useMemo, useState } from "react";
import { useFindOne, useGlobalAction, useUser } from "@gadgetinc/react";
import { api } from "../api";
import {
  PRIVACY_AGREEMENT_FULL_TEXT,
  PRIVACY_AGREEMENT_LAST_UPDATED_AT,
  PRIVACY_AGREEMENT_TLDR,
  PRIVACY_AGREEMENT_VERSION
} from "../../src/privacyAgreement";

export default function PrivacyAgreementGate() {
  const user = useUser(api);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [{}, acceptPrivacyAgreement] = useGlobalAction(api.acceptPrivacyAgreement);

  const [{ data: userRecord, fetching }] = useFindOne(
    api.user,
    user?.id,
    {
      select: {
        id: true,
        lastPrivacyAgreementSignedAt: true,
        lastPrivacyAgreementUpdatedAt: true
      },
      pause: !user?.id
    }
  );

  const requiresAcknowledgement = useMemo(() => {
    if (!user?.id || fetching || !userRecord) return false;
    const latestUpdate = new Date(PRIVACY_AGREEMENT_LAST_UPDATED_AT).getTime();
    const acknowledgedUpdate = userRecord.lastPrivacyAgreementUpdatedAt
      ? new Date(userRecord.lastPrivacyAgreementUpdatedAt).getTime()
      : 0;
    return !acknowledgedUpdate || acknowledgedUpdate < latestUpdate;
  }, [user?.id, fetching, userRecord]);

  if (!user?.id || fetching || !requiresAcknowledgement) {
    return null;
  }

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      await acceptPrivacyAgreement();
      window.location.reload();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = () => {
    window.location.href = "/sign-out";
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        background: "rgba(7, 11, 17, 0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px"
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "880px",
          maxHeight: "90vh",
          background: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}
      >
        <div style={{ padding: "18px 20px", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ fontWeight: 700, fontSize: "18px", color: "#0f172a" }}>
            Privacy & Proprietary Use Agreement
          </div>
          <div style={{ fontSize: "12px", color: "#475569", marginTop: "4px" }}>
            Version {PRIVACY_AGREEMENT_VERSION}
          </div>
        </div>

        <div style={{ overflowY: "auto", padding: "16px 20px" }}>
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #dbeafe",
              borderRadius: "10px",
              padding: "14px",
              marginBottom: "14px"
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: "8px", color: "#1e3a8a" }}>
              TL;DR
            </div>
            <ul style={{ margin: 0, paddingLeft: "18px", color: "#1e293b" }}>
              {PRIVACY_AGREEMENT_TLDR.map((item) => (
                <li key={item} style={{ marginBottom: "4px" }}>{item}</li>
              ))}
            </ul>
          </div>

          <pre
            style={{
              margin: 0,
              whiteSpace: "pre-wrap",
              fontFamily: "inherit",
              color: "#111827",
              lineHeight: 1.5,
              fontSize: "14px"
            }}
          >
            {PRIVACY_AGREEMENT_FULL_TEXT}
          </pre>
        </div>

        <div
          style={{
            borderTop: "1px solid #e5e7eb",
            padding: "14px 20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px"
          }}
        >
          <button
            type="button"
            onClick={handleDecline}
            style={{
              padding: "10px 14px",
              borderRadius: "8px",
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#334155",
              cursor: "pointer"
            }}
          >
            Decline and Sign Out
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={isSubmitting}
            style={{
              padding: "10px 14px",
              borderRadius: "8px",
              border: "none",
              background: isSubmitting ? "#93c5fd" : "#1d4ed8",
              color: "#ffffff",
              cursor: isSubmitting ? "default" : "pointer",
              fontWeight: 600
            }}
          >
            {isSubmitting ? "Recording..." : "I Acknowledge and Agree"}
          </button>
        </div>
      </div>
    </div>
  );
}
